import type { RelatedTemplate } from "AdminPanel/Spine/store/configStore";
import { blobToDataURL } from "AdminPanel/Spine/editor/create-avatar/blobToDataURL";
import { exportSpineVapVideos } from "AdminPanel/Spine/editor/export/components/export-vap/exportSpineVapVideos";
import {
  DEFAULT_VAP_ENCODE_LIMITS,
  normalizeVapEncodeLimits,
  type VapEncodeLimits,
} from "AdminPanel/Spine/editor/export/components/export-vap/vapConfig";
import { compositeStaticStackToWebpDataUrl } from "AdminPanel/Spine/lib/compositeStaticStackImage";
import { spineEditorSelectionHasDynamicSpine } from "AdminPanel/Spine/lib/hasDynamicSpineSelection";
import type { StaticStackItem } from "AdminPanel/Spine/lib/staticLayerStack";
import { postFetcher } from "api/swr";
import { makeShortId } from "commons";

export type CreateSpineAvatarUploadParams = {
  tid: string;
  drawDataJson: string;
  selectedSkins: string[];
  templateConfig: { skus?: Record<string, any> } | null | undefined;
  spineData: unknown | null | undefined;
  loadedRelatedTemplates: RelatedTemplate[] | undefined;
  spineApp: any;
  spineCanvas: any;
  previewMergedStaticStack: StaticStackItem[];
  fullscreenType?: number;
  encodeLimits?: Partial<VapEncodeLimits> | null;
  name?: string;
  desc?: string;
  defaultSet?: number;
  onExportProgress?: (progress: number) => void;
  onUploadStart?: () => void;
};

export type CreateSpineAvatarUploadResult = {
  id: string;
  mode: "video" | "image";
};

/**
 * Spine 编辑器创建 / 套装保存：
 * - 有动态 Spine 选中 → VAP + POST /spine-avatars
 * - 仅静态 → webp + POST /spine-avatars（不传 video）
 */
export async function createSpineAvatarUpload(
  params: CreateSpineAvatarUploadParams,
): Promise<CreateSpineAvatarUploadResult> {
  const {
    tid,
    drawDataJson,
    selectedSkins,
    templateConfig,
    spineData,
    loadedRelatedTemplates,
    spineApp,
    spineCanvas,
    previewMergedStaticStack,
    fullscreenType,
    encodeLimits,
    name,
    desc,
    defaultSet,
    onExportProgress,
  } = params;

  const hasDynamic = spineEditorSelectionHasDynamicSpine(
    selectedSkins,
    templateConfig,
    spineData,
    loadedRelatedTemplates,
  );
  const id = makeShortId(32);
  const defaultSetFields =
    defaultSet !== undefined && defaultSet !== null
      ? { defaultSet: Number(defaultSet) }
      : {};

  if (!hasDynamic) {
    onExportProgress?.(0.3);
    const imageDataUrl = await compositeStaticStackToWebpDataUrl(
      previewMergedStaticStack,
    );
    onExportProgress?.(0.7);
    params.onUploadStart?.();
    const uploadRes = await postFetcher(`/spine-avatars/${id}/upload-avatar`, {
      arg: { image: imageDataUrl },
    });
    await postFetcher("/spine-avatars", {
      arg: {
        id,
        name: name?.trim() || "",
        desc: desc?.trim() || "",
        drawData: drawDataJson,
        tid,
        clientVer: "0",
        app: "designer",
        contentLang: 9,
        url: uploadRes.url,
        bucket: uploadRes.bucket,
        engine: "spine",
        fullscreenType: fullscreenType ?? 0,
        ...defaultSetFields,
      },
    });
    return { id, mode: "image" };
  }

  const limits = normalizeVapEncodeLimits(
    encodeLimits ?? DEFAULT_VAP_ENCODE_LIMITS,
  );
  const { original, preview } = await exportSpineVapVideos({
    app: spineApp,
    spineCanvas,
    staticStackItems: previewMergedStaticStack,
    encodeLimits: limits,
    fullscreenType,
    onProgress: onExportProgress,
  });

  params.onUploadStart?.();
  const [originalDataUrl, previewDataUrl] = await Promise.all([
    blobToDataURL(original),
    blobToDataURL(preview),
  ]);
  const videoUploadRes = await postFetcher(`/spine-avatars/${id}/upload-video`, {
    arg: { video: originalDataUrl },
  });
  const previewUploadRes = await postFetcher(
    `/spine-avatars/${id}/upload-video-preview`,
    { arg: { video: previewDataUrl } },
  );

  await postFetcher("/spine-avatars", {
    arg: {
      id,
      name: name?.trim() || "",
      desc: desc?.trim() || "",
      drawData: drawDataJson,
      tid,
      clientVer: "0",
      app: "designer",
      contentLang: 9,
      videoUrl: videoUploadRes.videoUrl,
      videoPreviewUrl: previewUploadRes.videoPreviewUrl,
      bucket: videoUploadRes.bucket,
      engine: "spine",
      fullscreenType: fullscreenType ?? 0,
      ...defaultSetFields,
    },
  });

  return { id, mode: "video" };
}
